import React, {
    useEffect,
    useMemo,
    useState,
    useContext,
    useCallback,
} from "react";
import { UserWrapper } from "./Styled";
import Title from "./Title";
import TabNavigation from "./TabNavigation";
import { api } from "../../../shared/hooks/api";
import { useNavigate, useParams } from "react-router-dom";
import { User } from "../../../data/entities";
import Spinner from "../../../shared/components/LoadingPage";
import { getStoredAuthToken } from "../../../utils/authToken";
import { jwtDecode } from "jwt-decode";
import { error, success } from "../../../utils/toast";
import useMergeState from "../../../shared/hooks/useMergeState";
import BackButton from "../../../shared/components/BackButton";
import { AuthenticationContext } from "../../../provider/AuthenticationProvider";
import { ROLE } from "../../../data/enums";
import { WarningUnsavedChanges } from "../../../configs/warningProductModal";
import CancelModal from "../../../shared/components/CancelModal";

const initialPassword = {
    old_password: "",
    new_password: "",
};

const processUser = (data: User) => {
    return { ...data };
};

const UserProfile = () => {
    const { userId } = useParams();
    const [validated, setValidated] = useState(false);
    const [userPassword, setUserPassword] = useMergeState(initialPassword);
    const { user: currentUser } = useContext(AuthenticationContext);
    const isManager =
        currentUser?.role === ROLE.MANAGER ||
        currentUser?.role === ROLE.ACCOUNTANT_MANAGER;
    const navigate = useNavigate();
    const [isModified, setIsModified] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [navigationPath, setNavigationPath] = useState("");

    const [{ data, isLoading, setLocalData, setLocalUrl }, fetchUser] = api.get(
        `/users/${userId}`
    );
    const [{ isUpdating: isUpdatingUser, setLocalUrl: setUpdateUrl }, updateUser] = api.patch(
        `/users/${userId}/`
    );

    const [{ isUpdating: isUpdatingPassword, setLocalUrl: setUpdatePasswordUrl }, updateUserPassword] =
        api.patch(`/users/change_password/${userId}/`);

    const userData = useMemo(() => processUser(data), [data]);

    useEffect(() => {
        setLocalUrl(`/users/${userId}`);
    }, [userId, setLocalUrl]);

    useEffect(() => {
        setUpdateUrl(`/users/${userId}/`);
        setUpdatePasswordUrl(`/users/change_password/${userId}/`);
    }, [userId, setUpdatePasswordUrl, setUpdateUrl]);

    const markAsModified = useCallback(() => {
        setIsModified(true);
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (isModified) {
                event.preventDefault();
                event.returnValue = "";
                return "";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);

    const handleSaveAndNavigate = async () => {
        setShowUnsavedDialog(false);
        try {
            await handleUserInfo();
            navigate(navigationPath);
        } catch (err) {
            error("Cập nhập tài khoản thất bại", err);
        }
    };

    const handleDontSaveAndNavigate = () => {
        setShowUnsavedDialog(false);
        navigate(navigationPath);
    };

    const handleDataChange = useCallback(
        (updateUser: any) => {
            markAsModified();
            setLocalData({
                ...data,
                ...updateUser,
            });
        },
        [markAsModified, setLocalData, data]
    );

    const confirmNavigation = useCallback(
        (path: string) => {
            if (isModified) {
                setShowUnsavedDialog(true);
                setNavigationPath(path);
                return false;
            }

            navigate(path);
            return true;
        },
        [isModified, navigate]
    );

    const handleBackClick = () => {
        confirmNavigation("/users/");
    };

    const handleContinueEditing = () => {
        setShowUnsavedDialog(false);
    };

    const isOwnProfile = () => {
        const token = getStoredAuthToken();
        if (!token) return "";
        try {
            const decodedToken: any = jwtDecode(token);
            return decodedToken.user_id === userData.id;
        } catch (error) {
            return false;
        }
    };

    const handleUserInfo = async () => {
        if (isUpdatingUser) return;
        try {
            await updateUser(userData);
            await fetchUser();
            setValidated(true);
            
            // Update localStorage to refresh navbar avatar
            const updatedUser = userData;
            localStorage.setItem('user', JSON.stringify(updatedUser));
            window.dispatchEvent(new Event('storage'));
            
            success("THÀNH CÔNG", "Cập nhật tài khoản thành công");
        } catch (err) {
            error("Cập nhật tài khoản thất bại");
        }
    };

    const handleUpdatePassword = async () => {
        if (isUpdatingPassword) return;
        try {
            await updateUserPassword(userPassword);
            await fetchUser();
            setValidated(true);
            success("THÀNH CÔNG", "Cập nhật mật khẩu tài khoản thành công");
        } catch (err) {
            error("Cập nhật mật khẩu tài khoản thất bại", err);
        }
    };

    return (
        <UserWrapper>
            <BackButton label="Trở về" onClick={handleBackClick} />
            <Title
                title={userData.display_name}
                setValidated={setValidated}
                isOwnProfile={isOwnProfile() as boolean}
                handleSubmit={handleUserInfo}
                handleUpdatePassword={handleUpdatePassword}
                isManager={isManager}
                onDataChange={markAsModified}
                hasUnsavedChanges={isModified}
                onCancelClick={() => confirmNavigation("/users/")}
                isSavingInfo={isUpdatingUser}
                isSavingPassword={isUpdatingPassword}
            />
            {isLoading ? (
                <Spinner />
            ) : (
                <TabNavigation
                    validated={validated}
                    requiredData={data}
                    isOwnProfile={isOwnProfile() as boolean}
                    mergeUser={handleDataChange}
                    mergeUserPassword={setUserPassword}
                />
            )}
            <CancelModal
                open={showUnsavedDialog}
                handleConfirm={handleSaveAndNavigate}
                handleClose={handleDontSaveAndNavigate}
                handleCancel={handleContinueEditing}
                title={WarningUnsavedChanges.title}
                contentText={WarningUnsavedChanges.context}
                closeButtonText={WarningUnsavedChanges.closeButtonText}
                confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                showCancelButton={true}
                isLoading={isUpdatingUser || isUpdatingPassword}
            />
        </UserWrapper>
    );
};

export default UserProfile;
